import type { ReactNode } from 'react';
import { toast, type ExternalToast } from 'sonner';

import { ToastWithAvatar, type ToastAvatar, type ToastVariant } from './toast-content';

export interface AppToastOptions extends Omit<ExternalToast, 'description' | 'duration'> {
  description?: ReactNode;
  duration?: number | null;
  avatar?: ToastAvatar;
}

interface ShowToastConfig extends AppToastOptions {
  variant?: ToastVariant;
}

function normalizeDuration(duration?: number | null) {
  if (duration === null) {
    return Number.POSITIVE_INFINITY;
  }

  return duration;
}

function toExternalToast(options: AppToastOptions = {}): ExternalToast {
  const { duration, ...rest } = options;

  return {
    ...rest,
    duration: normalizeDuration(duration),
  };
}

function dispatchToast(
  title: ReactNode,
  variant: ToastVariant,
  options: AppToastOptions = {}
): string | number {
  const resolvedOptions = toExternalToast(options);

  if (variant === 'success') {
    return toast.success(title, resolvedOptions);
  }

  if (variant === 'error') {
    return toast.error(title, resolvedOptions);
  }

  if (variant === 'warning') {
    return toast.warning(title, resolvedOptions);
  }

  if (variant === 'info') {
    return toast.info(title, resolvedOptions);
  }

  if (variant === 'loading') {
    return toast.loading(title, resolvedOptions);
  }

  return toast.message(title, resolvedOptions);
}

export function showAvatarToast(
  title: ReactNode,
  variant: ToastVariant,
  avatar: ToastAvatar,
  options: AppToastOptions = {}
) {
  const { description, action, cancel, dismissible, id, duration } = options;
  const rest = { ...options };
  delete rest.description;
  delete rest.action;
  delete rest.cancel;
  delete rest.dismissible;
  delete rest.id;
  delete rest.duration;
  delete rest.avatar;

  return toast.custom(
    (toastId) => (
      <ToastWithAvatar
        toastId={toastId}
        title={title}
        description={description}
        variant={variant}
        avatar={avatar}
        action={action}
        cancel={cancel}
        dismissible={dismissible}
      />
    ),
    {
      ...rest,
      id,
      dismissible,
      duration: normalizeDuration(duration),
    }
  );
}

export function showToast(
  title: ReactNode,
  variantOrConfig: ToastVariant | ShowToastConfig = 'default',
  duration?: number | null,
  options: Omit<AppToastOptions, 'duration'> = {}
) {
  const resolvedConfig =
    typeof variantOrConfig === 'string'
      ? {
          variant: variantOrConfig,
          options: { ...options, duration },
        }
      : (() => {
          const { variant = 'default', duration: configDuration, ...rest } = variantOrConfig;

          return {
            variant,
            options: {
              ...rest,
              duration: configDuration ?? duration,
            },
          };
        })();

  if (resolvedConfig.options.avatar) {
    return showAvatarToast(
      title,
      resolvedConfig.variant,
      resolvedConfig.options.avatar,
      resolvedConfig.options
    );
  }

  return dispatchToast(title, resolvedConfig.variant, resolvedConfig.options);
}

export function dismissToast(toastId?: string | number) {
  return toast.dismiss(toastId);
}

export function useAppToast() {
  return {
    showToast,
    showAvatarToast,
    dismissToast,
    success: (title: ReactNode, options?: AppToastOptions) =>
      showToast(title, 'success', options?.duration, options),
    error: (title: ReactNode, options?: AppToastOptions) =>
      showToast(title, 'error', options?.duration, options),
    warning: (title: ReactNode, options?: AppToastOptions) =>
      showToast(title, 'warning', options?.duration, options),
    info: (title: ReactNode, options?: AppToastOptions) =>
      showToast(title, 'info', options?.duration, options),
    loading: (title: ReactNode, options?: AppToastOptions) =>
      showToast(title, 'loading', options?.duration, options),
  };
}

export default useAppToast;
